import React, { useState, useEffect, useCallback } from 'react';
import client from '../api/client';
import { C, Toast, Spinner, useNotify } from '../utils/styles';

const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '—';
const STATUS_COLORS = { trial:'#f59e0b', active:'#22c55e', trial_expired:'#ef4444', cancelled:'#9ca3af', past_due:'#ef4444' };
const STATUS_LABELS = { trial:'Trial Active', active:'Active', trial_expired:'Trial Expired', cancelled:'Cancelled', past_due:'Past Due' };

const PlanIcon = ({ name }) => {
  const n = (name||'').toLowerCase();
  if (n.includes('white') || n.includes('whitelabel')) return <i className="ti ti-crown" style={{fontSize:20}}/>;
  if (n.includes('agency')) return <i className="ti ti-building-store" style={{fontSize:20}}/>;
  return <i className="ti ti-bolt" style={{fontSize:20}}/>;
};

export default function Billing() {
  const [pageLoading,   setPageLoading]   = useState(true);
  const [plansLoading,  setPlansLoading]  = useState(true);
  const [account,       setAccount]       = useState(null);
  const [plans,         setPlans]         = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [showUpgrade,   setShowUpgrade]   = useState(false);
  const [paying,        setPaying]        = useState('');
  const [billingCycle,  setBillingCycle]  = useState('monthly'); // 'monthly' | 'yearly'
  const [notification,  notify]           = useNotify();

  // ── Load account info ─────────────────────────────────────
  const fetchAccount = useCallback(async () => {
    try {
      const { data } = await client.get('/api/accounts/me');
      setAccount(data || {});
    } catch (err) {
      // Non-fatal — show page with limited info
      console.error('Account fetch error:', err?.response?.data?.error || err?.message);
      setAccount({});
    } finally { setPageLoading(false); }
  }, []); // eslint-disable-line

  // ── Load plans — try multiple endpoints ───────────────────
  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    let loaded = false;

    // Try 1: accounts/plans/all (deployed since day 1)
    try {
      const { data } = await client.get('/api/accounts/plans/all');
      if (data && data.length > 0) {
        setPlans(data);
        loaded = true;
      }
    } catch {}

    // Try 2: billing/plans/public (added in billing_backend_additions.js)
    if (!loaded) {
      try {
        const { data } = await client.get('/api/billing/plans/public');
        if (data && data.length > 0) {
          setPlans(data);
          loaded = true;
        }
      } catch {}
    }

    if (!loaded) setPlans([]);
    setPlansLoading(false);
  }, []);

  // ── Load payment history ──────────────────────────────────
  const fetchPayments = useCallback(async () => {
    try {
      const { data } = await client.get('/api/billing/payments');
      setPayments(data || []);
    } catch {} // silent — payments endpoint may not be deployed yet
  }, []);

  useEffect(() => {
    fetchAccount();
    fetchPlans();
    fetchPayments();
  }, [fetchAccount, fetchPlans, fetchPayments]);

  // ── Razorpay ──────────────────────────────────────────────
  const handleRazorpay = async (plan) => {
    const price = billingCycle === 'yearly'
      ? (plan.price_yearly_inr || plan.price_monthly_inr * 10)
      : plan.price_monthly_inr;

    setPaying(plan.id);
    try {
      const { data: order } = await client.post('/api/billing/razorpay/create-order', {
        plan_id:        plan.id,
        billing_cycle:  billingCycle,
        amount:         price,
      });
      const options = {
        key:         order.key_id,
        amount:      order.amount,
        currency:    order.currency || 'INR',
        name:        'Digital Xtension',
        description: `${plan.name} — ${billingCycle === 'yearly' ? 'Annual' : 'Monthly'}`,
        order_id:    order.order_id,
        handler: async (response) => {
          try {
            await client.post('/api/billing/razorpay/verify', {
              order_id:      order.order_id,
              payment_id:    response.razorpay_payment_id,
              signature:     response.razorpay_signature,
              plan_id:       plan.id,
              billing_cycle: billingCycle,
            });
            notify('✅ Payment successful! Subscription activated.');
            setShowUpgrade(false);
            fetchAccount();
            fetchPayments();
          } catch (err) { notify(err.response?.data?.error || 'Verification failed', 'error'); }
        },
        prefill: { name: account?.name, email: account?.email },
        theme:   { color: C.primary },
        modal:   { ondismiss: () => setPaying('') },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to initiate payment', 'error');
      setPaying('');
    }
  };

  const handlePayPal = async (plan) => {
    setPaying(plan.id);
    try {
      const { data } = await client.post('/api/billing/paypal/create-order', {
        plan_id:       plan.id,
        billing_cycle: billingCycle,
      });
      window.location.href = data.approve_url;
    } catch (err) {
      notify(err.response?.data?.error || 'PayPal failed', 'error');
      setPaying('');
    }
  };

  // ── Trial math ────────────────────────────────────────────
  const now         = new Date();
  const trialEnd    = account?.trial_ends_at ? new Date(account.trial_ends_at) : null;
  const daysLeft    = trialEnd ? Math.max(0, Math.ceil((trialEnd - now) / 86400000)) : 0;
  const status      = account?.subscription_status || 'trial';
  const currentPlan = plans.find(p => p.id === account?.plan_id);
  const trialDays   = currentPlan?.trial_days || 14;
  const trialPct    = trialEnd && trialDays > 0
    ? Math.min(100, Math.round(((trialDays - daysLeft) / trialDays) * 100))
    : 0;

  const getStatusDesc = () => {
    if (status === 'active')        return `Next billing: ${fmtDate(account?.next_billing_date)}`;
    if (status === 'trial_expired') return 'Your trial has expired — select a plan to continue';
    if (status === 'cancelled')     return 'Subscription cancelled';
    if (status === 'trial') {
      if (trialEnd && daysLeft > 0)  return `${daysLeft} day${daysLeft!==1?'s':''} left · Ends ${fmtDate(account.trial_ends_at)}`;
      if (trialEnd && daysLeft === 0) return 'Trial ends today — upgrade to keep access';
      return 'Trial active — upgrade before it expires';
    }
    return '';
  };

  // ── Price helpers ─────────────────────────────────────────
  const getPlanPrice = (plan) => {
    if (billingCycle === 'yearly' && plan.price_yearly_inr > 0) return plan.price_yearly_inr;
    return plan.price_monthly_inr || 0;
  };
  const getYearlySaving = (plan) => {
    if (!plan.price_yearly_inr || !plan.price_monthly_inr) return 0;
    const yearlyEquiv = plan.price_monthly_inr * 12;
    return Math.round(((yearlyEquiv - plan.price_yearly_inr) / yearlyEquiv) * 100);
  };

  if (pageLoading) return <Spinner/>;

  const cardStyle = { background:'#fff', borderRadius:16, border:`1px solid ${C.border}`, padding:24, marginBottom:16 };

  return (
    <div style={{ maxWidth:860, margin:'0 auto' }}>
      <Toast {...notification}/>

      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:20, fontWeight:700, color:C.dark, marginBottom:2 }}>Billing & Subscription</h1>
        <p style={{ fontSize:13, color:C.muted }}>Manage your plan, payments and subscription</p>
      </div>

      {/* ── Status card ── */}
      <div style={cardStyle}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:56, height:56, borderRadius:14, background:`${STATUS_COLORS[status]}18`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <i className={`ti ${status==='active'?'ti-crown':status==='trial'?'ti-clock':'ti-alert-circle'}`}
                style={{ fontSize:26, color:STATUS_COLORS[status] }}/>
            </div>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                <span style={{ fontWeight:700, fontSize:18, color:C.dark }}>
                  {currentPlan?.name || (account?.plan_id ? 'Loading plan...' : 'No Plan Selected')}
                </span>
                <span style={{ background:`${STATUS_COLORS[status]}18`, color:STATUS_COLORS[status], borderRadius:20, padding:'2px 12px', fontSize:12, fontWeight:600 }}>
                  {STATUS_LABELS[status] || status}
                </span>
              </div>
              <div style={{ fontSize:13, color:C.muted }}>{getStatusDesc()}</div>
            </div>
          </div>
          <button onClick={() => setShowUpgrade(v => !v)}
            style={{ padding:'10px 20px', borderRadius:10, border:'none', background:C.primary, color:'#fff', fontWeight:600, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
            <i className="ti ti-crown"/>
            {status === 'active' ? 'Change Plan' : 'Upgrade Now'}
          </button>
        </div>

        {/* Trial progress bar */}
        {status === 'trial' && trialEnd && daysLeft > 0 && (
          <div style={{ marginTop:20, paddingTop:20, borderTop:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:12 }}>
              <span style={{ color:C.muted }}>Trial progress</span>
              <span style={{ color:daysLeft<=3?C.danger:daysLeft<=7?C.warning:C.success, fontWeight:600 }}>
                {daysLeft} of {trialDays} days remaining
              </span>
            </div>
            <div style={{ background:'#f3f4f6', borderRadius:6, height:8 }}>
              <div style={{ width:`${trialPct}%`, height:8, borderRadius:6, background:daysLeft<=3?C.danger:daysLeft<=7?C.warning:C.success, transition:'width 0.4s' }}/>
            </div>
          </div>
        )}
      </div>

      {/* ── Plan selection ── */}
      {showUpgrade && (
        <div style={cardStyle}>
          {/* Header + close */}
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 }}>
            <div>
              <div style={{ fontWeight:700, fontSize:16, color:C.dark, marginBottom:4 }}>Choose a Plan</div>
              <div style={{ fontSize:13, color:C.muted }}>All plans include a free trial. Cancel anytime.</div>
            </div>
            <button onClick={() => setShowUpgrade(false)}
              style={{ background:'#f3f4f6', border:'none', borderRadius:8, width:32, height:32, cursor:'pointer', color:C.muted, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <i className="ti ti-x"/>
            </button>
          </div>

          {/* ── Monthly / Yearly toggle ── */}
          <div style={{ display:'flex', justifyContent:'center', marginBottom:24 }}>
            <div style={{ background:'#f3f4f6', borderRadius:10, padding:4, display:'flex', gap:2 }}>
              {[
                { id:'monthly', label:'Monthly' },
                { id:'yearly',  label:'Yearly' },
              ].map(opt => (
                <button key={opt.id} onClick={() => setBillingCycle(opt.id)}
                  style={{ padding:'8px 24px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, transition:'all 0.2s',
                    background: billingCycle===opt.id ? '#fff' : 'transparent',
                    color:      billingCycle===opt.id ? C.primary : C.muted,
                    boxShadow:  billingCycle===opt.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  }}>
                  {opt.label}
                  {opt.id === 'yearly' && (
                    <span style={{ marginLeft:6, background:'#22c55e', color:'#fff', borderRadius:20, padding:'1px 8px', fontSize:10, fontWeight:700 }}>
                      SAVE UP TO 20%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Plan cards */}
          {plansLoading ? (
            <div style={{ textAlign:'center', padding:'32px 0', color:C.faint }}>
              <i className="ti ti-loader ti-spin" style={{ fontSize:28, display:'block', marginBottom:10, color:C.primary }}/>
              <div style={{ fontSize:13 }}>Loading plans...</div>
            </div>
          ) : plans.length === 0 ? (
            <div style={{ textAlign:'center', padding:'32px 0', color:C.faint }}>
              <i className="ti ti-credit-card" style={{ fontSize:36, display:'block', marginBottom:10, color:'#d1d5db' }}/>
              <div style={{ fontWeight:500, color:C.muted, marginBottom:6 }}>No plans available</div>
              <div style={{ fontSize:12 }}>Plans haven't been configured yet. Please contact support or check back later.</div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:14 }}>
              {plans.map(p => {
                const isCurrent  = p.id === account?.plan_id;
                const isPopular  = p.is_popular;
                const price      = getPlanPrice(p);
                const saving     = getYearlySaving(p);
                const isPaying   = paying === p.id;
                return (
                  <div key={p.id} style={{
                    borderRadius:12, padding:20, position:'relative',
                    border:`2px solid ${isCurrent?C.primary:isPopular?'#f59e0b':C.border}`,
                    background: isCurrent?'var(--color-primary-light)':'#fff',
                  }}>
                    {isPopular && !isCurrent && (
                      <div style={{ position:'absolute', top:-11, left:'50%', transform:'translateX(-50%)', background:'#f59e0b', color:'#fff', borderRadius:20, padding:'2px 14px', fontSize:10, fontWeight:700, whiteSpace:'nowrap' }}>
                        MOST POPULAR
                      </div>
                    )}
                    {isCurrent && (
                      <div style={{ position:'absolute', top:-11, right:14, background:C.primary, color:'#fff', borderRadius:20, padding:'2px 12px', fontSize:10, fontWeight:700 }}>
                        CURRENT
                      </div>
                    )}
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                      <div style={{ width:40, height:40, borderRadius:10, background:isCurrent?`${C.primary}22`:`${C.muted}11`, display:'flex', alignItems:'center', justifyContent:'center', color:isCurrent?C.primary:C.muted }}>
                        <PlanIcon name={p.name}/>
                      </div>
                      <div style={{ fontWeight:700, fontSize:16, color:isCurrent?C.primary:C.dark }}>{p.name}</div>
                    </div>
                    {p.description && <div style={{ fontSize:12, color:C.muted, marginBottom:10 }}>{p.description}</div>}

                    {/* Price display */}
                    <div style={{ marginBottom:14 }}>
                      <span style={{ fontSize:28, fontWeight:800, color:isCurrent?C.primary:C.dark }}>
                        {price === 0 ? 'Free' : `₹${price.toLocaleString()}`}
                      </span>
                      {price > 0 && (
                        <span style={{ fontSize:13, color:C.muted }}>
                          {billingCycle === 'yearly' ? '/year' : '/month'}
                        </span>
                      )}
                      {billingCycle === 'yearly' && saving > 0 && (
                        <div style={{ fontSize:11, color:'#16a34a', marginTop:3, fontWeight:600 }}>
                          <i className="ti ti-tag" style={{ marginRight:4 }}/>Save {saving}% vs monthly
                        </div>
                      )}
                      {billingCycle === 'monthly' && p.price_yearly_inr > 0 && (
                        <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>
                          Or ₹{(p.price_yearly_inr).toLocaleString()}/year (save {saving}%)
                        </div>
                      )}
                      {(p.trial_days||0) > 0 && (
                        <div style={{ fontSize:11, color:'#16a34a', marginTop:3 }}>
                          <i className="ti ti-gift" style={{ marginRight:4 }}/>{p.trial_days}-day free trial
                        </div>
                      )}
                    </div>

                    {/* Feature list from JSONB */}
                    {p.features && (
                      <ul style={{ listStyle:'none', margin:'0 0 14px', padding:0, display:'grid', gap:6 }}>
                        {[
                          p.features.contacts_limit !== undefined && (p.features.contacts_limit === -1 ? 'Unlimited contacts' : `${Number(p.features.contacts_limit||0).toLocaleString()} contacts`),
                          p.features.users_limit    !== undefined && (p.features.users_limit    === -1 ? 'Unlimited users'    : `${p.features.users_limit} users`),
                          p.sub_account_limit       !== undefined && (p.sub_account_limit       === -1 ? 'Unlimited sub-accounts' : `${p.sub_account_limit} sub-account${p.sub_account_limit!==1?'s':''}`),
                          p.features.white_label    && 'White-label branding',
                          p.features.custom_domain  && 'Custom domain',
                          p.features.ai             && 'AI features',
                        ].filter(Boolean).map((f,i) => (
                          <li key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:C.muted }}>
                            <i className="ti ti-check" style={{ color:'#22c55e', fontSize:13, flexShrink:0 }}/>{f}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Pay button */}
                    {isCurrent ? (
                      <div style={{ padding:'9px', borderRadius:8, background:'#f3f4f6', color:C.muted, fontSize:13, fontWeight:500, textAlign:'center' }}>Current Plan</div>
                    ) : price === 0 ? (
                      <div style={{ padding:'9px', borderRadius:8, background:'#f3f4f6', color:C.muted, fontSize:13, textAlign:'center' }}>Free — No payment needed</div>
                    ) : (
                      <>
                        <button onClick={() => handleRazorpay(p)} disabled={!!paying}
                          style={{ width:'100%', padding:'10px', borderRadius:8, border:'none', background:isPaying?'#e5e7eb':C.primary, color:isPaying?C.muted:'#fff', fontWeight:600, fontSize:13, cursor:paying?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginBottom:6 }}>
                          {isPaying
                            ? <><i className="ti ti-loader ti-spin"/>Processing...</>
                            : <><i className="ti ti-credit-card"/>Pay ₹{price.toLocaleString()} · {billingCycle === 'yearly' ? 'Annual' : 'Monthly'}</>}
                        </button>
                        {p.price_monthly_usd > 0 && (
                          <button onClick={() => handlePayPal(p)} disabled={!!paying}
                            style={{ width:'100%', padding:'8px', borderRadius:8, border:`1px solid ${C.border}`, background:'#fff', color:'#374151', fontSize:12, cursor:paying?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, fontWeight:500 }}>
                            <i className="ti ti-brand-paypal" style={{ color:'#003087' }}/> PayPal (${p.price_monthly_usd}/mo)
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop:16, fontSize:12, color:C.faint, textAlign:'center' }}>
            <i className="ti ti-lock" style={{ marginRight:4 }}/>Secure payment via Razorpay · Cancel anytime · No hidden fees
          </div>
        </div>
      )}

      {/* ── Current plan details ── */}
      {currentPlan && (
        <div style={cardStyle}>
          <div style={{ fontWeight:700, fontSize:15, color:C.dark, marginBottom:14 }}>Plan Details</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
            {[
              ['Plan',         currentPlan.name],
              ['Monthly',      currentPlan.price_monthly_inr>0 ? `₹${currentPlan.price_monthly_inr.toLocaleString()}/mo` : 'Free'],
              ['Annual',       currentPlan.price_yearly_inr>0  ? `₹${currentPlan.price_yearly_inr.toLocaleString()}/yr`  : '—'],
              ['Status',       STATUS_LABELS[status]||status],
              ['Trial Days',   currentPlan.trial_days>0 ? `${currentPlan.trial_days} days` : 'No trial'],
              ['Trial Ends',   account?.trial_ends_at ? fmtDate(account.trial_ends_at) : '—'],
              ['Next Billing', account?.next_billing_date ? fmtDate(account.next_billing_date) : '—'],
              ['Sub-accounts', currentPlan.sub_account_limit===-1?'Unlimited':(currentPlan.sub_account_limit||'—')],
              ['Contacts',     currentPlan.features?.contacts_limit===-1?'Unlimited':(currentPlan.features?.contacts_limit?.toLocaleString()||'—')],
            ].map(([k,v]) => (
              <div key={k} style={{ background:'#f9fafb', borderRadius:10, padding:'12px 14px' }}>
                <div style={{ fontSize:10, fontWeight:600, color:C.faint, letterSpacing:0.5, marginBottom:4 }}>{k.toUpperCase()}</div>
                <div style={{ fontSize:13, fontWeight:500, color:C.dark }}>{v||'—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Payment history ── */}
      <div style={cardStyle}>
        <div style={{ fontWeight:700, fontSize:15, color:C.dark, marginBottom:14 }}>Payment History</div>
        {!payments.length ? (
          <div style={{ textAlign:'center', padding:'32px 0', color:C.faint }}>
            <i className="ti ti-receipt" style={{ fontSize:36, display:'block', marginBottom:10, color:'#d1d5db' }}/>
            <div style={{ fontWeight:500, color:C.muted, marginBottom:4 }}>No payments yet</div>
            <div style={{ fontSize:12 }}>Your payment history will appear here after your first transaction.</div>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${C.border}`, background:'#f9fafb' }}>
                  {['Date','Plan','Cycle','Amount','Gateway','Status'].map(h=>(
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', color:C.muted, fontWeight:500, fontSize:12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map((p,i) => (
                  <tr key={p.id||i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ padding:'10px 14px' }}>{fmtDate(p.created_at)}</td>
                    <td style={{ padding:'10px 14px', fontWeight:500 }}>{p.plan_name||'—'}</td>
                    <td style={{ padding:'10px 14px', textTransform:'capitalize' }}>{p.metadata?.billing_cycle||'monthly'}</td>
                    <td style={{ padding:'10px 14px', fontWeight:600 }}>
                      {p.currency==='INR'?`₹${((p.amount||0)/100).toLocaleString()}`:`$${((p.amount||0)/100).toFixed(2)}`}
                    </td>
                    <td style={{ padding:'10px 14px', textTransform:'capitalize' }}>{p.gateway||'—'}</td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ background:p.status==='success'?'#f0fdf4':'#fef2f2', color:p.status==='success'?'#16a34a':'#ef4444', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:500 }}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
